import {
  LinkArrow,
  PenIcon,
  PlusIcon,
  RightArrowIcon,
  TrashAltIcon
} from "assets/icons";
import cn from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon, IconButton, Loader } from "ui";

import { LocationInput } from "./atoms";
import { DeliveryLocationColumnProps } from "./DeliveryLocationColumn.types";

import styles from "./DeliveryLocationColumn.module.scss";

const DeliveryLocationColumn = ({
  title,
  isDisabled,
  isLoading,
  locationsList,
  onLocationSelect,
  selectedLocation,
  onLocationCreate,
  onLocationUpdate,
  onLocationDelete
}: DeliveryLocationColumnProps) => {
  const { t } = useTranslation();

  const [creatingLocation, setCreatingLocation] = useState<string | null>(null);
  const [updatingLocation, setUpdatingLocation] = useState<string | null>(null);
  const [updatingLocationNewName, setUpdatingLocationNewName] = useState<
    string | null
  >(null);

  const onPlusBtnClick = () => {
    setCreatingLocation("");
    onLocationSelect?.(null);
    setUpdatingLocation?.(null);
    setUpdatingLocationNewName?.(null);
  };

  useEffect(() => {
    setCreatingLocation?.(null);
    setUpdatingLocation?.(null);
    setUpdatingLocationNewName?.(null);
  }, [locationsList]);

  return (
    <div className={styles.column}>
      <div className={styles.column_header}>
        <div className={styles.column_title}>{t(title)}</div>

        <button
          className={cn("reset-button", styles.column_plusBtn)}
          disabled={isDisabled}
          onClick={onPlusBtnClick}
        >
          <Icon as={PlusIcon} boxSize={24} color="#fff" />
        </button>
      </div>

      {isLoading && <Loader className={styles.loader} />}

      {creatingLocation !== null && (
        <LocationInput
          value={creatingLocation}
          onChange={(e) => setCreatingLocation(e.target.value)}
          isConfirmDisabled={creatingLocation.length === 0}
          onConfirm={() => {
            onLocationCreate(creatingLocation);
          }}
          onCrossClick={() => {
            setCreatingLocation(null);
          }}
        />
      )}

      {locationsList?.map((location) =>
        location.name === updatingLocation &&
        updatingLocationNewName !== null ? (
          <LocationInput
            key={location.name}
            value={updatingLocationNewName}
            onChange={(e) => setUpdatingLocationNewName(e.target.value)}
            isConfirmDisabled={
              updatingLocationNewName.length === 0 ||
              updatingLocationNewName === updatingLocation
            }
            onConfirm={() => {
              onLocationUpdate({
                existingName: updatingLocation,
                newName: updatingLocationNewName
              });
            }}
            onCrossClick={() => {
              setUpdatingLocation(null);
              setUpdatingLocationNewName(null);
            }}
          />
        ) : (
          <div
            key={location.name}
            className={cn(styles.location, {
              [styles.location__selected]: selectedLocation === location.name
            })}
            onClick={() => {
              onLocationSelect?.(location.name);
              setCreatingLocation(null);
              setUpdatingLocation(null);
              setUpdatingLocationNewName(null);
            }}
          >
            <div className={styles.location_name}>{location.name}</div>

            <IconButton
              icon={<PenIcon color="#A8A8BB" width={20} height={20} />}
              onClick={(e) => {
                e.stopPropagation();
                setCreatingLocation(null);
                setUpdatingLocation(location.name);
                setUpdatingLocationNewName(location.name);
              }}
            />

            {location.deliveryZoneId && (
              <Link
                className={styles.location_linkToFee}
                to={{
                  pathname: `/delivery-fees/fees`,
                  search: `?initialSearch=${location.name}`
                }}
                target="_blank"
              >
                <Icon as={LinkArrow} boxSize={20} />
              </Link>
            )}

            {location.canBeDeleted && (
              <IconButton
                icon={<TrashAltIcon fill="#ff0033" width={24} height={24} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onLocationDelete(location.name);
                }}
              />
            )}

            {onLocationSelect && (
              <Icon as={RightArrowIcon} boxSize={24} color="#000" />
            )}
          </div>
        )
      )}
    </div>
  );
};

export default DeliveryLocationColumn;
